import { useState, useEffect } from 'react';
import { useSchool } from '@/contexts/SchoolContext';
import { useSchoolData } from '@/hooks';
import gradesService from '@/services/gradesService';
import { organizationalUnitService } from '@/services/OrganizationalUnitService';
import { loadCalendarAudienceOptions } from '@/services/calendar/calendarAudienceService';
import { inspectPhaseAudience } from '../utils/calendarAudienceUtils';

const SCOPE_OPTIONS = [
  { value: 'everyone', label: 'Whole school' },
  { value: 'phases', label: 'By Phase' },
  { value: 'grades', label: 'By Grade' },
  { value: 'classes', label: 'By Class' },
  { value: 'committees', label: 'By Committee' },
];

/**
 * AudiencePicker - Select who should see a calendar event.
 *
 * Props:
 *   value: { scope, phases, gradeIds, classIds, committeeIds }
 *   onChange: (audience) => void
 */
export default function AudiencePicker({ value, onChange }) {
  const { schoolId } = useSchool();
  const { classes } = useSchoolData();
  const [grades, setGrades] = useState([]);
  const [committees, setCommittees] = useState([]);
  const [phases, setPhases] = useState([]);
  const [phaseError, setPhaseError] = useState(null);

  const scope = value?.scope || 'everyone';
  const phaseAudience = inspectPhaseAudience(value);
  const selectedPhaseIds = phaseAudience.representation === 'canonical' ? phaseAudience.references : [];
  const selectedGradeIds = value?.gradeIds || [];
  const selectedClassIds = value?.classIds || [];
  const selectedCommitteeIds = value?.committeeIds || [];

  // Load grades and committees
  useEffect(() => {
    if (!schoolId) return;
    gradesService.getAll(schoolId).then(setGrades).catch(() => setGrades([]));
  }, [schoolId]);

  useEffect(() => {
    if (!schoolId) return;
    organizationalUnitService
      .getUnitsByType(schoolId, 'committee')
      .then(setCommittees)
      .catch(() => setCommittees([]));
  }, [schoolId]);

  useEffect(() => {
    let active = true;
    if (!schoolId) {
      setPhases([]);
      return () => { active = false; };
    }
    setPhaseError(null);
    loadCalendarAudienceOptions(schoolId)
      .then((result) => { if (active) setPhases(result.phases || []); })
      .catch(() => {
        if (active) {
          setPhases([]);
          setPhaseError('School phases are unavailable. Try again before saving this audience.');
        }
      });
    return () => { active = false; };
  }, [schoolId]);

  const handleScopeChange = (newScope) => {
    if (newScope === 'phases') {
      onChange({ scope: 'phases', phaseReferenceVersion: 1, phaseIds: [] });
      return;
    }
    onChange({ scope: newScope, ...(newScope === 'grades' ? { gradeIds: [] } : {}),
      ...(newScope === 'classes' ? { classIds: [] } : {}),
      ...(newScope === 'committees' ? { committeeIds: [] } : {}) });
  };

  const toggleItem = (field, id) => {
    const current = value?.[field] || [];
    const next = current.includes(id)
      ? current.filter(x => x !== id)
      : [...current, id];
    onChange({ ...value, [field]: next });
  };

  const togglePhase = (id) => {
    const next = selectedPhaseIds.includes(id)
      ? selectedPhaseIds.filter((phaseId) => phaseId !== id)
      : [...selectedPhaseIds, id];
    onChange({ scope: 'phases', phaseReferenceVersion: 1, phaseIds: next });
  };

  // Deduplicate classes by grade for a cleaner display
  const sortedClasses = [...(classes || [])].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '')
  );

  const sortedGrades = [...grades].sort((a, b) => (a.order || 0) - (b.order || 0));

  const scopeHelp = {
    everyone: 'Visible to staff and families linked to this school.',
    phases: 'Visible to staff and families in the selected phases.',
    grades: 'Visible to staff and families in the selected grades.',
    classes: 'Visible to staff and families in the selected classes.',
    committees: 'Visible to staff only, for the selected committees.',
  }[scope];

  return (
    <fieldset className="space-y-3">
      <legend className="text-sm font-medium">Audience</legend>

      {/* Scope selector */}
      <div className="flex flex-wrap gap-2" aria-label="Event audience">
        {SCOPE_OPTIONS.map(opt => (
          <button
            key={opt.value}
            type="button"
            className={`btn btn-sm ${scope === opt.value ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => handleScopeChange(opt.value)}
            aria-pressed={scope === opt.value}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <p className="text-xs text-base-content/60">{scopeHelp}</p>

      {/* Phase checkboxes */}
      {scope === 'phases' && (
        <div className="space-y-2 pt-1">
          {phaseAudience.representation === 'legacy' && (
            <div className="rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-sm">
              This event uses an older phase audience. Choose the current school phases before saving.
            </div>
          )}
          {phaseError && <p className="text-sm text-error">{phaseError}</p>}
          <div className="flex flex-wrap gap-3">
          {phases.map(phase => (
            <label key={phase.id} className="label cursor-pointer gap-2">
              <input
                type="checkbox"
                className="checkbox checkbox-sm checkbox-primary"
                checked={selectedPhaseIds.includes(phase.id)}
                onChange={() => togglePhase(phase.id)}
              />
              <span className="label-text text-sm">{phase.name}</span>
            </label>
          ))}
          {!phaseError && phases.length === 0 && (
            <p className="text-sm text-base-content/50">No school phases are configured yet.</p>
          )}
          </div>
        </div>
      )}

      {/* Grade multi-select */}
      {scope === 'grades' && (
        <div className="flex flex-wrap gap-3 pt-1">
          {sortedGrades.map(grade => (
            <label key={grade.id} className="label cursor-pointer gap-2">
              <input
                type="checkbox"
                className="checkbox checkbox-sm checkbox-primary"
                checked={selectedGradeIds.includes(grade.id)}
                onChange={() => toggleItem('gradeIds', grade.id)}
              />
              <span className="label-text text-sm">{grade.label || grade.value || grade.id}</span>
            </label>
          ))}
          {sortedGrades.length === 0 && (
            <p className="text-sm text-base-content/50">No grades configured yet.</p>
          )}
        </div>
      )}

      {/* Class multi-select */}
      {scope === 'classes' && (
        <div className="flex flex-wrap gap-3 pt-1">
          {sortedClasses.map(cls => (
            <label key={cls.id} className="label cursor-pointer gap-2">
              <input
                type="checkbox"
                className="checkbox checkbox-sm checkbox-primary"
                checked={selectedClassIds.includes(cls.id)}
                onChange={() => toggleItem('classIds', cls.id)}
              />
              <span className="label-text text-sm">{cls.name}</span>
            </label>
          ))}
          {sortedClasses.length === 0 && (
            <p className="text-sm text-base-content/50">No classes configured yet.</p>
          )}
        </div>
      )}

      {/* Committee multi-select */}
      {scope === 'committees' && (
        <div className="flex flex-wrap gap-3 pt-1">
          {committees.map(comm => (
            <label key={comm.id} className="label cursor-pointer gap-2">
              <input
                type="checkbox"
                className="checkbox checkbox-sm checkbox-primary"
                checked={selectedCommitteeIds.includes(comm.id)}
                onChange={() => toggleItem('committeeIds', comm.id)}
              />
              <span className="label-text text-sm">{comm.name}</span>
            </label>
          ))}
          {committees.length === 0 && (
            <p className="text-sm text-base-content/50">No committees configured yet. Create them via Teams.</p>
          )}
        </div>
      )}
    </fieldset>
  );
}
